import { useState, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import DistrictSalesMap from "../Maps/DistrictSalesMap";
import { getSalesDashboard } from "../../services/salesDashboard";

// Sales Stats counts orders; same district map as Product Sales
const METRICS = [
  ["revenue", "Revenue"],
  ["orders", "Orders", "Orders", "orders"],
];

const IndiaMap = ({ districtWise, districtUnmapped, selectedState, onStateClick, productsByState, startDate, endDate }) => {
  const [expanded, setExpanded] = useState({});
  const [district, setDistrict] = useState(null); // {key, state, district}
  const [districtProducts, setDistrictProducts] = useState(null);
  const [districtError, setDistrictError] = useState(false);

  // Products sold in the clicked district (same dates and statuses as the page)
  useEffect(() => {
    if (!district) return;
    setDistrictProducts(null);
    setDistrictError(false);
    const params = { district: district.key, ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) };
    getSalesDashboard(params)
      .then((res) => setDistrictProducts(res.data.products_by_district || []))
      .catch(() => setDistrictError(true));
  }, [district, startDate, endDate]);

  // Clicking the open district again closes it
  const clickDistrict = (d) => setDistrict((cur) => (cur?.key === d.key ? null : d));
  const totals = (districtProducts || []).reduce(
    (t, g) => ({ units: t.units + g.units, amount: t.amount + g.amount_raw }),
    { units: 0, amount: 0 }
  );
  const fmtInr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  const districtPanel = district && (
    <div className="flex max-h-[560px] flex-col rounded-lg border border-gray-100 p-2">
      <div className="mb-2 flex items-start justify-between gap-2 px-1">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-gray-900">Products sold in {district.district}</p>
          <p className="text-xs text-gray-500">{district.state}</p>
          {districtProducts && (
            <p className="mt-1 text-xs text-gray-700">
              <b>{totals.units.toLocaleString("en-IN")}</b> units · <b>{fmtInr(totals.amount)}</b>
            </p>
          )}
        </div>
        <button onClick={() => setDistrict(null)} className="rounded p-1 text-gray-500 hover:bg-gray-100" title="Close">
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {districtError && <p className="p-2 text-xs text-red-600">Could not load this district's products.</p>}
        {!districtError && !districtProducts && <p className="p-2 text-xs text-gray-500">Loading…</p>}
        {districtProducts?.length === 0 && <p className="p-2 text-xs text-gray-500">No products sold here in these dates.</p>}
        {districtProducts?.map((g) => (
          <div key={g.category} className="py-1.5">
            <div className="flex items-center justify-between px-1 text-xs">
              <span className="font-semibold text-gray-700">{g.category}</span>
              <span className="text-gray-500">{g.units} units · {g.amount}</span>
            </div>
            {g.products.map((p) => (
              <div key={p.name} className="mx-1 mt-1 flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1">
                <span className="min-w-0 truncate text-[11px] text-gray-700">{p.name}</span>
                <span className="shrink-0 text-[11px] text-gray-600">
                  {p.units_sold} × · <b>{p.amount}</b>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  // Collapse expanded categories when a different state is selected
  useEffect(() => {
    setExpanded({});
  }, [selectedState]);

  const toggleCategory = (category) =>
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));

  const showProducts = !!selectedState && productsByState?.length > 0;
  const districts = districtWise || [];
  const unmapped = districtUnmapped || {};
  const mapData = {
    districts,
    unmapped,
    total_units: districts.reduce((sum, d) => sum + d.orders, 0) + (unmapped.no_address || 0) + (unmapped.not_placed || 0),
  };
  // Picking "All India" clears the state; picking (or clicking) a state zooms into it.
  // Changing state closes the district panel.
  const changeState = (state) => {
    setDistrict(null);
    onStateClick?.(state === "all" ? selectedState : state);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
          State-wise Sales Volume
        </p>
        {selectedState && (
          <button
            onClick={() => changeState("all")}
            className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100 transition-colors"
            title="Clear state filter"
          >
            {selectedState}
            <span className="text-emerald-500">✕</span>
          </button>
        )}
      </div>

      <DistrictSalesMap
        data={mapData}
        metrics={METRICS}
        zoom={selectedState || "all"}
        onZoomChange={changeState}
        selectedDistrict={district?.key}
        onDistrictClick={clickDistrict}
        sidePanel={districtPanel}
      />
      {!district && (
        <p className="mt-1 text-[11px] text-gray-500">
          {selectedState ? "Click a district to see the products sold there." : "Click a state to zoom in."}
        </p>
      )}

        {/* Category-wise products for the selected state */}
        {showProducts && (
          <div className="mt-4 max-h-[420px] overflow-y-auto border border-gray-100 rounded-lg p-2">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 px-1">
              Category-wise Sales · {selectedState}
            </p>
            <div className="divide-y divide-gray-100">
              {productsByState.map((cat) => {
                const isOpen = !!expanded[cat.category];
                return (
                  <div key={cat.category}>
                    <button
                      onClick={() => toggleCategory(cat.category)}
                      className="w-full flex items-center justify-between gap-2 py-1.5 px-1 text-left hover:bg-gray-50 rounded transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">
                          {cat.category}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {cat.units} unit{cat.units === 1 ? "" : "s"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs font-semibold text-gray-600">
                          {cat.amount}
                        </span>
                        <ChevronDown
                          size={13}
                          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="mb-1.5 mx-1 rounded bg-gray-50 border border-gray-100 divide-y divide-gray-100">
                        {(cat.products || []).map((p) => (
                          <div
                            key={p.name}
                            className="flex items-center justify-between gap-2 px-2 py-1"
                          >
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 truncate">{p.name}</p>
                              <p className="text-[10px] text-gray-400">{p.units_sold} sold</p>
                            </div>
                            <span className="text-[11px] font-semibold text-gray-600 shrink-0">
                              {p.amount}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
};

export default IndiaMap;
